/**
 * Lightweight analytics helper.
 *
 * - Lazy-loads gtag.js when `VITE_GA_ID` is set (e.g. `G-XXXXXXX`).
 * - Always pushes events to `window.dataLayer` so GTM also captures them.
 * - Safe to call from any environment: no-ops on SSR / when window is missing.
 *
 * Convention: event names are snake_case grouped by feature
 * (`quiz_*`, `compare_*`, `referral_*`) so they're easy to filter
 * in GA4's "Conversions" / "Events" reports.
 */

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }

  const gaId = (import.meta as any).env?.VITE_GA_ID as string | undefined;
  if (gaId && typeof document !== "undefined") {
    // Skip if already injected (e.g. by GTM).
    if (!document.querySelector(`script[data-ga-id="${gaId}"]`)) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
      s.dataset.gaId = gaId;
      document.head.appendChild(s);
      window.gtag!("js", new Date());
      window.gtag!("config", gaId, { send_page_view: true });
    }
  }
}

/** Send a custom event. Names should be snake_case. */
export function track(event: string, params: Record<string, any> = {}): void {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...params });
    if (typeof window.gtag === "function") {
      window.gtag("event", event, params);
    }
  } catch {
    /* swallow — analytics must never break the app */
  }
}

/** Convenience wrappers — keeps event names + params consistent across callsites. */
export const analytics = {
  // Quiz "Recomendado para ti en 30s"
  quizStart: (params: { lang: string; source?: string } = { lang: "es" }) =>
    track("quiz_start", params),
  quizGoalSelect: (params: { goal: string; lang: string }) =>
    track("quiz_goal_select", params),
  quizRecommend: (params: { goal: string; count: number; total: number; lang: string }) =>
    track("quiz_recommend", params),
  quizCheckout: (params: { goal: string; items: number; total: number; lang: string }) =>
    track("quiz_checkout", { ...params, conversion: true }),

  // Comparador
  compareAdd: (params: { product_id: string | number; slot: number; lang: string }) =>
    track("compare_add", params),
  compareView: (params: { items: number; lang: string }) =>
    track("compare_view", params),
  compareCheckout: (params: { product_id: string | number; price: number; lang: string }) =>
    track("compare_checkout", { ...params, conversion: true }),
  compareClear: (params: { lang: string }) => track("compare_clear", params),

  // Referidos
  referralFooterClick: (params: { lang: string }) => track("referral_footer_click", params),
  referralCopy: (params: { has_code: boolean; lang: string }) =>
    track("referral_copy", { ...params, conversion: true }),
  referralShareWhatsapp: (params: { has_code: boolean; lang: string }) =>
    track("referral_share_whatsapp", { ...params, conversion: true }),
  referralSignupClick: (params: { source: string; lang: string }) =>
    track("referral_signup_click", params),
  referralApplySubmit: (params: { channel: string; status: "ok" | "error" }) =>
    track("referral_apply_submit", { ...params, conversion: params.status === "ok" }),
};